var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { datadogLogs } from '@datadog/browser-logs';
import * as qs from 'qs';
import { addProtocolIfMissing } from '../add-protocol-if-missing';
import { formatUtm } from '../format-utm';
var replaceLTMsWithUTMs = function (payload) {
    return payload.replace(/ltm_(?<verb>(term|campaign|content|source|medium))/gm, 'utm_$<verb>');
};
var emptyUTMsObject = {
    utmSource: '',
};
var parseUTMsFromURLOrPath = function (payload) {
    if (!payload)
        return emptyUTMsObject;
    var parsedPayload = payload.indexOf('utm') === -1 ? replaceLTMsWithUTMs(payload) : payload;
    var parsedQueryString = qs.parse(parsedPayload, { ignoreQueryPrefix: true });
    var parsedUTMs = formatUtm(parsedQueryString);
    var nestedParamsPossible = Boolean(!(parsedUTMs === null || parsedUTMs === void 0 ? void 0 : parsedUTMs.utmSource) && (parsedQueryString === null || parsedQueryString === void 0 ? void 0 : parsedQueryString.returnURL));
    if (nestedParamsPossible) {
        var parsedReturnURLParams = parseUTMsFromURLOrPath(new URL(addProtocolIfMissing(parsedQueryString.returnURL)).search);
        return __assign(__assign({}, parsedUTMs), parsedReturnURLParams);
    }
    return parsedUTMs;
};
var parseUTMsFromCurrentUrl = function () {
    var _a;
    try {
        return parseUTMsFromURLOrPath((_a = location === null || location === void 0 ? void 0 : location.search) !== null && _a !== void 0 ? _a : '');
    }
    catch (error) {
        datadogLogs.logger.error("An error occurred parsing location.search, see errorDetails for more information.", {
            errorDetails: {
                error: error,
                locationSearch: location === null || location === void 0 ? void 0 : location.search,
            },
        });
        return emptyUTMsObject;
    }
};
/**
 * parseUTMsFromReturnURL()
 *
 * Sources UTM data from returnURL if provided.
 *
 * @param returnUrl
 */
var parseUTMsFromReturnURL = function (returnUrl) {
    try {
        if (!returnUrl)
            return emptyUTMsObject;
        var builtReturnURL = new URL(addProtocolIfMissing(returnUrl));
        return parseUTMsFromURLOrPath(builtReturnURL.search);
    }
    catch (error) {
        datadogLogs.logger.error("An error occurred parsing UTMs from returnURL, see errorDetails for more information.", {
            errorDetails: {
                error: error,
                returnURL: returnUrl !== null && returnUrl !== void 0 ? returnUrl : '',
            },
        });
        return emptyUTMsObject;
    }
};
/**
 * parseUTMsFromAccountOrigin()
 *
 * Sources UTMs from accountOrigin if provided.
 *
 * @param accountOrigin
 */
var parseUTMsFromAccountOrigin = function (accountOrigin) {
    try {
        if (!accountOrigin)
            return emptyUTMsObject;
        var builtAccountOrigin = new URL(addProtocolIfMissing(accountOrigin));
        return parseUTMsFromURLOrPath(builtAccountOrigin.search);
    }
    catch (error) {
        datadogLogs.logger.error("An error occurred parsing account origin, see errorDetails for more information.", {
            errorDetails: {
                error: error,
                accountOrigin: accountOrigin,
            },
        });
        return emptyUTMsObject;
    }
};
var isValidUTMData = function (utmData) {
    try {
        return !!(utmData === null || utmData === void 0 ? void 0 : utmData.utmSource);
    }
    catch (_a) {
        return false;
    }
};
/**
 * Retrieve UTMs is used to retrieve UTMs with consideration for fallbacks and niche flows.
 *
 * The fallback flow is as follows:
 *
 * - If UTMs are in lead data, the lead UTM data will be used.
 * - If UTMs are found in the current URL (location?.search), the current URL UTM data will be used.
 * - If UTMs are found using the account origin set in the lead data, the account origin UTM data will be used.
 * - If UTMs are found in the returnURL provided by LALA, the returnURL UTM data will be used.
 *
 * In order for lead data to be considered valid, it must contain utmSource with a value, the rest are optional.
 *
 * ie:
 *
 * ```
 * {
 *   utmSource: 'some-utm-source'
 * }
 * ```
 *
 * @param leadData
 * @param returnUrl - Provided by LALA due to Domain redirection flow, absolute fallback for sourcing UTMs
 */
export var retrieveUtm = function (leadData, returnUrl) {
    var _a;
    try {
        var leadDataUTMs = ((_a = leadData === null || leadData === void 0 ? void 0 : leadData.utm) === null || _a === void 0 ? void 0 : _a.utmSource) ? leadData === null || leadData === void 0 ? void 0 : leadData.utm : emptyUTMsObject;
        var currentUrlUTMs = parseUTMsFromCurrentUrl();
        var accountOriginUTMs = parseUTMsFromAccountOrigin(leadData === null || leadData === void 0 ? void 0 : leadData.accountOrigin);
        var returnUrlUTMs = parseUTMsFromReturnURL(returnUrl);
        if (isValidUTMData(leadDataUTMs))
            return leadDataUTMs;
        if (isValidUTMData(currentUrlUTMs))
            return currentUrlUTMs;
        if (isValidUTMData(accountOriginUTMs))
            return accountOriginUTMs;
        if (isValidUTMData(returnUrlUTMs))
            return returnUrlUTMs;
        return false;
    }
    catch (error) {
        datadogLogs.logger.error("An error occurred while attempting to retrieve UTMs.", {}, error);
        return false;
    }
};
//# sourceMappingURL=index.js.map